import { ColDef, ColumnState, DateFilterModel, TextFilterModel, SetFilterModel } from 'ag-grid-community';
import { Component, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectAchInstructions, selectAchRecurringProfiles, selectChecksStandingAuth, selectHasCreateStandingAuthAccess, selectRegistrationType, selectHasNoAchStandingInstructions, selectHasNoCheckStandingInstructions, selectHasNoItStandingInstructions, selectHasNoStandingInstructions, selectHasNoWireStandingInstructions, selectHeaderStateServiceError, selectInstructions, selectIsDemoUser, selectIsEmployee, selectIsIip, selectIsPtrs, selectItStandingAuth, selectTransactionAuthorizationDetails, selectWiresStandingAuth } from '../../../account-info-header/reducers';
import { AchActions, CheckActions, InternalTransferActions, StandingAuthorizationsTableActions, WireActions } from '../../../create-request/actions';
import { gridReady } from '../../../create-request/actions/standing-authorizations-table.actions';
import { defaultColumnTemplate, sharedColNames } from '../../../create-request/components/transactions-table/table-helpers';
import { ToAccountInfo } from '../../../create-request/models/internal-transfer/ToAccountInfo';
import { TransactionAuthorization } from '../../../create-request/models/overview/TransactionAuthorization';
import { ActionsLinkComponent } from '../../../shared/actions-link/actions-link.component';
import { SloaNicknameComponent } from '../../../shared/components/sloa-nickname/sloa-nickname.component';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import { agFilterNames } from '../../../shared/types/AgFilterNames';
import { ComponentState } from '../../../shared/types/ComponentState';
import { StandingAuthTableColumn } from '../../../shared/types/StandingAuthTableColumn';
import { StandingInstruction } from '../../../shared/types/StandingInstruction';
import { TableComponentState, selectedRowData } from '../../../shared/types/TableComponentState';
import { TransactionDirection, TransactionType } from '../../../shared/types/Transaction';
import { cancelModalSourceTypes, CreateTypes, sloaAuthorization, tableActions, transactionDirection, transactionTypes } from '../../../shared/utilities/Constants';
import { StandingAuthAchActions, StandingAuthCheckActions } from '../../../standing-authorizations/actions';
import { selectAchTransferTab, selectAllTab, selectChecksTab, selectInternalTransferTab, selectPreferencesLoaded, selectWiresTab } from '../../../user-preference/reducers';
import { SharedActions } from '../../actions';
import { displayCancelModal } from '../../actions/shared.actions';
import { selectCancelModalContinueClicked, selectCancelModalSource, selectCreateTypeInContext, selectIsCreateTransaction, selectStandingAuthTableCurrentTab, selectStandingAuthTableState } from '../../reducers';
import { navRouteConstants } from '../../utilities/Constants';
import { ContextService } from 'projects/integration/src/app/context.service';
import { CoreService } from '@charlesschwab/sac-ui-core';
import {Constants} from '../../utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-standing-authorizations-table',
  templateUrl: './standing-authorizations-table.component.html',
  styleUrls: ['./standing-authorizations-table.component.scss'],
})
export class StandingAuthorizationsTableComponent implements OnInit {
  @Input() showCancelModalOnClick: boolean;
  private deleteClicked$ = new BehaviorSubject<boolean>(false);
  deleteLinkClickedAsObservable$ = this.deleteClicked$.asObservable();
  private currentSelectedTransaction$ = new BehaviorSubject<string | null>(null);
  currentSelectedTransactionAsObservable$ = this.currentSelectedTransaction$.asObservable();
  @Output() editLinkClicked = new EventEmitter<boolean>();
  toAccountInfo: ToAccountInfo;
  destroy$ = new Subject<void>();
  hasCreateStandingAuthAccess: boolean;
  isEmployee: boolean;
  isDemoUser: boolean;
  isPtrs: boolean;
  registrationType: string;
  currentTab: string;
  columnState: ColumnState[] | undefined;
  isLoading = false;
  colNames = {
    direction: 'Funds direction',
    date: 'Transaction date',
    frequency: 'Frequency',
    amount: 'Amount',
    toFromAccount: sharedColNames.toFromAccount,
    transactionType: sharedColNames.transactionType,
    actions: sharedColNames.actions,
    status: 'Status'
  };
  columnDefs: ColDef[];

  defaultColumnDefs: ColDef[] = [
    {
      field: 'Type',
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 300
    },
    {
      field: 'Nickname',
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      cellRenderer: SloaNicknameComponent,
      flex: 1,
      minWidth: 120
    },
    {
      field: sharedColNames.toFromAccount,
      floatingFilterComponent: 'customTextFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      flex: 1,
      minWidth: 450
    },
    {
      field: sharedColNames.status,
      filter: agFilterNames.set,
      floatingFilterComponent: 'customSetFloatingFilter',
      floatingFilterComponentParams: {
        suppressFilterButton: true,
      },
      minWidth: 120,
      width: 120,
      maxWidth: 130
    },
    {
      field: this.colNames.actions,
      filter: false,
      suppressMovable: true,
      sortable: false,
      cellRenderer: ActionsLinkComponent,
      cellRendererParams: {
        onClick: this.actionLinkClicked.bind(this),
        viewStatus: tableActions.viewDetails,
        startTransaction: tableActions.startTransaction,
        editProfile: tableActions.edit,
        deleteProfile: tableActions.deleteProfile,
        deactivateProfile: tableActions.deactivateProfile,
        reactivateProfile: tableActions.reactivateProfile,
        tableName: 'standing authorizations'
      },
      minWidth: 305,
      maxWidth: 305
    },
  ];

  noExportCol: string[] = ['Actions'];
  fileName: string;

  filterModel: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };

  rowData$: Observable<StandingAuthTableColumn[]>;
  standingAuthTableState$: Observable<TableComponentState>;
  tableComponentState$: Observable<ComponentState>;
  isIip: boolean;
  hasHeaderStateError: boolean = false;
  private unlistener: () => void;
  transactionAuthorizationDetails: TransactionAuthorization;
  statusColumnIndex: number = 3;
  createTransactionPage: boolean;
  hasNoStandingInstructions: boolean;
  hasNoAchStandingInstructions: boolean;
  hasNoCheckStandingInstructions: boolean;
  hasNoItStandingInstructions: boolean;
  hasNoWireStandingInstructions: boolean;
  currentActionLinkClickedEvent: any;
  createTypeInContext: CreateTypes;

  constructor(
    private store: Store,
    private namePipe: NamePipe,
    private renderer2: Renderer2,
    private router: Router,
    private toastr: ToastrService,
    private contextService: ContextService,
    private coreService: CoreService
  ) { }

  ngOnInit(): void {
    this.fileName = 'standing-authorizations-all-';

    this.store.select(selectHeaderStateServiceError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasHeaderStateError = error
    })

    this.store.select(selectStandingAuthTableCurrentTab).pipe(takeUntil(this.destroy$))
      .subscribe(currentTab => this.currentTab = currentTab);

    this.store.select(selectTransactionAuthorizationDetails).pipe(takeUntil(this.destroy$)).subscribe(transactionAuthorizationDetails =>
      this.transactionAuthorizationDetails = transactionAuthorizationDetails)

    this.standingAuthTableState$ = this.store.select(
      selectStandingAuthTableState
    );
    this.rowData$ = this.store.select(selectInstructions).pipe(
      withLatestFrom(this.store.select(selectAchRecurringProfiles)),
      map(([instructions, _recurringProfiles]) => {
        return [
          ...this.mapSloaToColumn(instructions)
        ]
      })
    );

    this.columnDefs = this.defaultColumnDefs;
    this.store.select(selectPreferencesLoaded)
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectAllTab)
        ),
        filter(([loaded]) => loaded === true)
      ).subscribe(([loaded, tab]) => {
      if (tab) {
        this.columnDefs = this.buildColumnPreferences(tab.columns);
      }
    });

    this.store.select(selectHasNoStandingInstructions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoInstructions => {
        this.hasNoStandingInstructions = hasNoInstructions;
        this.store.dispatch(SharedActions.hasEmptyResponse());
      });

    this.store.select(selectHasNoAchStandingInstructions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoInstructions => this.hasNoAchStandingInstructions = hasNoInstructions);

    this.store.select(selectHasNoCheckStandingInstructions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoInstructions => this.hasNoCheckStandingInstructions = hasNoInstructions);

    this.store.select(selectHasNoItStandingInstructions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoInstructions => this.hasNoItStandingInstructions = hasNoInstructions);

    this.store.select(selectHasNoWireStandingInstructions).pipe(takeUntil(this.destroy$))
      .subscribe(hasNoInstructions => this.hasNoWireStandingInstructions = hasNoInstructions);

    this.store.select(selectCreateTypeInContext).pipe(takeUntil(this.destroy$))
      .subscribe(currentContext => this.createTypeInContext = currentContext as CreateTypes);


    this.store
      .select(selectIsIip)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isIip) => (this.isIip = isIip));

    this.setActiveTableEventListener();

    this.store
      .select(selectHasCreateStandingAuthAccess)
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((hasAccess) => (this.hasCreateStandingAuthAccess = hasAccess));

    this.store
      .select(selectIsEmployee)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEmployee) => (this.isEmployee = isEmployee));

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

     this.store
      .select(selectRegistrationType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((regType) => (this.registrationType = regType));

    this.store.
      select(selectIsPtrs).
      pipe(takeUntil(this.destroy$)).
      subscribe(isPtrs => { this.isPtrs = isPtrs })

    this.store.select(selectIsCreateTransaction).pipe(takeUntil(this.destroy$)).subscribe(val => this.createTransactionPage = val);

    this.store.
      select(selectCancelModalContinueClicked).
      pipe(
        takeUntil(this.destroy$),
        withLatestFrom(this.store.select(selectCancelModalSource)),
        filter(([continueClicked, source]) => continueClicked && this.cancelClickedFromStandingAuthTable(source ?? ''))).
        subscribe(_ => {
      if (this.currentActionLinkClickedEvent?.name === tableActions.startTransaction) {
        if (this.currentActionLinkClickedEvent.rowData.Type.includes('ACH')) {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.ach,
            transferDirection: this.currentActionLinkClickedEvent.rowData.Type.includes('out')
              ? 'outgoing'
              : 'incoming',
            standingAuthId: this.currentActionLinkClickedEvent.rowData.StandingAuthId
          };
          this.store.dispatch(
            SharedActions.updatePreviousContext({createType: this.createTypeInContext})
          );
          this.store.dispatch(
            StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
          );
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Wire')) {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.wire,
            standingAuthId: this.currentActionLinkClickedEvent.rowData.StandingAuthId,
            isBundled: this.currentActionLinkClickedEvent.rowData.isBundled
          };
          this.store.dispatch(
            StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
          );
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Internal transfer')) {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.internalTransfer,
            standingAuthId: this.currentActionLinkClickedEvent.rowData.StandingAuthId,
            isBundled: this.currentActionLinkClickedEvent.rowData.isBundled
          };
          this.store.dispatch(
            StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
          );
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Check')) {
          let rowData: selectedRowData = {
            transactionType: transactionTypes.check,
            standingAuthId: this.currentActionLinkClickedEvent.rowData.StandingAuthId,
            isBundled: this.currentActionLinkClickedEvent.rowData.isBundled
          };
          this.store.dispatch(
            StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
          );
        }
      } else if (this.currentActionLinkClickedEvent?.name === tableActions.viewDetails) {
        if (this.currentActionLinkClickedEvent.rowData.Type.includes('Internal transfer')) {
          this.store
            .pipe(
              takeUntil(this.destroy$),
              select(selectItStandingAuth),
              map((element) =>
                element.find((instruction) => {
                  return (
                    instruction.cashieringRqstId ===
                    this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                  );
                })
              )
            )
            .subscribe((res) => {
              if (res) {
                this.store.dispatch(
                  StandingAuthorizationsTableActions.loadViewPage()
                );
                this.store.dispatch(
                  InternalTransferActions.callInquiry({
                    payload: res,
                    viewType: 'standing-auth',
                    transactionType: transactionTypes.internalTransfer,
                  })
                );
              }
            });
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('ACH')) {
          this.store.select(selectAchInstructions).pipe(
            takeUntil(this.destroy$),
            withLatestFrom(this.store.select(selectAchRecurringProfiles)),
            map(([instructions, recurringProfiles]) => {
              return [...instructions, ...recurringProfiles]
            }),
            map((instructionsInclRecurring) =>
              instructionsInclRecurring.find((instructionOrProfile) => {
                return (
                  instructionOrProfile.cashieringRqstId ===
                  this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                );
              })
            )
          ).subscribe((res) => {
            if (res) {
              this.store.dispatch(
                SharedActions.updatePreviousContext({createType: this.createTypeInContext})
              );
              this.store.dispatch(
                StandingAuthorizationsTableActions.loadViewPage()
              );
              this.store.dispatch(
                AchActions.callInquiry({
                  payload: res,
                  viewType: 'standing-auth',
                  transactionType: transactionTypes.ach,
                })
              );
            }
          });
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Wire')) {
          this.store
            .pipe(
              takeUntil(this.destroy$),
              select(selectWiresStandingAuth),
              map((element) =>
                element.find((instruction) => {
                  return (
                    instruction.cashieringRqstId ===
                    this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                  );
                })
              )
            )
            .subscribe((res) => {
              if (res) {
                this.store.dispatch(
                  StandingAuthorizationsTableActions.loadViewPage()
                );
                this.store.dispatch(
                  WireActions.callInquiry({
                    payload: res,
                    viewType: 'standing-auth',
                    transactionType: transactionTypes.wireSloa,
                    resourceVersion: '3'
                  })
                );
              }
            });
        } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Check')) {
          this.store
            .pipe(
              takeUntil(this.destroy$),
              select(selectChecksStandingAuth),
              map((element) =>
                element.find((instruction) => {
                  return (
                    instruction.cashieringRqstId ===
                    this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                  );
                })
              )
            )
            .subscribe((res) => {
              if (res) {
                this.store.dispatch(
                  StandingAuthorizationsTableActions.loadViewPage()
                );
                this.store.dispatch(
                  CheckActions.callInquiry({
                    payload: res,
                    viewType: 'standing-auth',
                    transactionType: transactionTypes.check,
                  })
                );
              }
            });
        }
      } else if (this.currentActionLinkClickedEvent?.name === tableActions.edit) {
          let rowData: selectedRowData = {
            transactionType: this.currentActionLinkClickedEvent.rowData.Type.includes('ACH')
              ? transactionTypes.ach
              : transactionTypes.check,
            transferDirection: this.currentActionLinkClickedEvent.rowData.Type.includes('out')
              ? 'outgoing'
              : 'incoming',
            standingAuthId: this.currentActionLinkClickedEvent.rowData.StandingAuthId,
            frequency: this.currentActionLinkClickedEvent.rowData.isRecurring ? 'recurring' : 'onetime'
          };
          this.store.dispatch(
            SharedActions.updatePreviousContext({createType: this.createTypeInContext})
          );
          this.store.dispatch(
            StandingAuthorizationsTableActions.editProfileClicked({ rowData })
          );

          if (this.currentActionLinkClickedEvent.rowData.Type.includes('ACH')) {
            this.store.select(selectAchInstructions).pipe(
              take(1),
              withLatestFrom(this.store.select(selectAchRecurringProfiles)),
              map(([instructions, recurringProfiles]) => {
                return [...instructions, ...recurringProfiles]
              }),
              map((instructionsInclRecurring) =>
                instructionsInclRecurring.find((instructionOrProfile) => {
                  return (
                    instructionOrProfile.cashieringRqstId ===
                    this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                  );
                })
              )
            ).subscribe((res) => {
              if (res) {
                this.store.dispatch(
                  SharedActions.updatePreviousContext({createType: this.createTypeInContext})
                );
                this.store.dispatch(
                  StandingAuthAchActions.callAchInquiry({
                    payload: res,
                    viewType: 'standing-auth',
                    transactionType: transactionTypes.ach,
                  })
                );
              }
            });
          } else if (this.currentActionLinkClickedEvent.rowData.Type.includes('Check')) {
            this.store.select(selectInstructions).pipe(
              take(1),
              map((instructions) =>
                instructions.find((instructionOrProfile) => {
                  return (
                    instructionOrProfile.cashieringRqstId ===
                    this.currentActionLinkClickedEvent.rowData['cashieringRqstId']
                  );
                })
              )
            ).subscribe((res) => {
              if (res) {
                this.store.dispatch(
                  StandingAuthCheckActions.callCheckInquiry({
                    payload: res,
                    viewType: 'standing-auth',
                    transactionType: transactionTypes.check,
                  })
                );
              }
            });
          }
        }
    });

    this.coreService.currentContextUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.fileName = 'standing-authorizations-all-';
        this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'All'}));
        this.rowData$ = this.store.select(selectInstructions).pipe(
            withLatestFrom(this.store.select(selectAchRecurringProfiles)),
            map(([instructions, _recurringProfiles]) => {
              return [
                ...this.mapSloaToColumn(instructions)
              ]
            })
          );
          if (this.hasNoStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
        }
        this.setActiveTableEventListener();
        this.scrollToTop();
    });
  }

  updateGridReady(ready: boolean) {
    this.store.dispatch(gridReady({ gridReady: ready }));

    this.store.select(selectAllTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
      this.columnState = tab?.columns;
    });
  }

  private setActiveTableEventListener(): void {
    this.unlistener = this.renderer2.listen(document, 'sdpsSelect', event => {
      // sdpsSelect is a custom Event from Everest
      // e.detail contains Id of sdpsSelct
      const e = event as CustomEvent
      //This is to pass fileName for export functionality
      this.fileName = `standing-authorizations-${e.detail}-`;
      switch (e.detail as string) {
        case 'all':
          this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'All'}));
          this.store.select(selectAllTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
            this.columnState = tab?.columns;
          });
          if (this.columnState) {
            this.columnDefs = this.buildColumnPreferences(this.columnState);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.rowData$ = this.store.select(selectInstructions).pipe(
            withLatestFrom(this.store.select(selectAchRecurringProfiles)),
            map(([instructions, _recurringProfiles]) => {
              return [
                ...this.mapSloaToColumn(instructions)
              ]
            })
          );
          if (this.hasNoStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
          }
          break;
        case 'ach':
          this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'ACH transfers'}));
          this.store.select(selectAchTransferTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
            this.columnState = tab?.columns;
          });
          if (this.columnState) {
            this.columnDefs = this.buildColumnPreferences(this.columnState);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.rowData$ = this.store.select(selectAchInstructions).pipe(
            withLatestFrom(this.store.select(selectAchRecurringProfiles)),
            map(([instructions, _recurringProfiles]) => {
              return [
                ...this.mapSloaToColumn(instructions)
              ]
            })
          );
          if (this.hasNoAchStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
          }
          break;
        case 'internal-transfers':
          this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'Internal transfers'}));
          this.store.select(selectInternalTransferTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
            this.columnState = tab?.columns;
          });
          if (this.columnState) {
            this.columnDefs = this.buildColumnPreferences(this.columnState);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.rowData$ = this.store.pipe(
            select(selectItStandingAuth),
            map((instructions) => this.mapSloaToColumn(instructions))
          );
          if (this.hasNoItStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
          }
          break;
        case 'wires':
          this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'Wires'}));
          this.store.select(selectWiresTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
            this.columnState = tab?.columns;
          });
          if (this.columnState) {
            this.columnDefs = this.buildColumnPreferences(this.columnState);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.rowData$ = this.store.pipe(
            select(selectWiresStandingAuth),
            map((instructions) => this.mapSloaToColumn(instructions))
          );
          if (this.hasNoWireStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
          }
          break;
        case 'checks':
          this.store.dispatch(StandingAuthorizationsTableActions.currentTabUpdated({currentTab: 'Checks'}));
          this.store.select(selectChecksTab).pipe(takeUntil(this.destroy$)).subscribe(tab => {
            this.columnState = tab?.columns;
          });
          if (this.columnState) {
            this.columnDefs = this.buildColumnPreferences(this.columnState);
          } else {
            this.columnDefs = this.defaultColumnDefs;
          }
          this.rowData$ = this.store.pipe(
            select(selectChecksStandingAuth),
            map((instructions) => this.mapSloaToColumn(instructions))
          );
          if (this.hasNoCheckStandingInstructions) {
            this.store.dispatch(SharedActions.hasEmptyResponse());
          }
          break;
        default:
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.unlistener();
  }

  mapSloaToColumn(
    instructions: StandingInstruction[]
  ): StandingAuthTableColumn[] {
    return instructions.map((standingAuth) => {
      var suffix = standingAuth.descriptionSuffix ?? '';
      let prefix = 'On-request';
      if (suffix.includes("Set-Up -")){
        let splitSuffix = suffix.split('-');
        suffix = splitSuffix[2];
      }
      if (standingAuth.frequencyDescription != null && standingAuth.frequencyDescription != undefined) {
        suffix += " - " + standingAuth.frequencyDescription;
        prefix = 'Recurring';
      }
      return {
        Type: this.mapTransactionType(
          standingAuth.transactionType,
          standingAuth.fundsDirection,
          suffix,
          prefix,
          standingAuth.incomingAmount,
          standingAuth.outgoingAmount
        ),
        Nickname: standingAuth.nickname,
        'To/From': this.namePipe.transform(
          this.mapToFromDisplayText(standingAuth)
        ),
        Status: standingAuth.statusCodeDescription,
        cashieringRqstId: standingAuth.cashieringRqstId,
        StandingAuthId: `${standingAuth.transactionType[0]}-${standingAuth.cashieringRqstId}-${standingAuth.cashieringRqstSecondId}`,
        hasRequestTransactionAccess:
          !this.hasHeaderStateError &&
          standingAuth.isRequestTransaction &&
          (this.isSloaAuthorized(standingAuth.transactionType) || this.isEmployee || this.isDemoUser),
        isIip: this.isIip,
        isRecurring: standingAuth.isRecurring || (standingAuth.frequencyDescription != null && standingAuth.frequencyDescription != undefined),
        IsEditableProfile: standingAuth.isEditable,
        IsDeletableProfile: standingAuth.isCancelable,
        IsReactivatableProfile: standingAuth.isReactivatable,
        isStandingInstruction: standingAuth.frequencyDescription == null,
        isBundled: standingAuth.isBundled ?? false,
        registrationType: this.registrationType,
        // Actions rendering would be handle by ActionLinkComponent
      };
    });
  }

  actionLinkClicked(event: any) {
    switch (event.name) {
      case tableActions.viewDetails:
        this.statusClicked(event);
        break;
      case tableActions.startTransaction:
        this.startTransactionClicked(event);
        break;
      case tableActions.deactivateProfile:
        this.deleteProfileClicked(event);
        (event);
        break;
      case tableActions.deleteProfile:
        this.deleteProfileClicked(event);
        break;
      case tableActions.edit:
        this.editProfileClicked(event);
        break;
      default:
        break;
    }
  }

  editProfileClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'StandingAuthTableEdit'
      }));
    } else {
      if (event.rowData.Type.includes('Check')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.check,
          standingAuthId: event.rowData.StandingAuthId,
          frequency: event.rowData.isRecurring ? 'recurring' : 'onetime'
        };
        this.store.dispatch(
          StandingAuthorizationsTableActions.editProfileClicked({ rowData })
        );

        this.store.select(selectInstructions).pipe(
          take(1),
          map((instructions) =>
            instructions.find((instructionOrProfile) => {
              return (
                instructionOrProfile.cashieringRqstId ===
                event.rowData['cashieringRqstId']
              );
            })
          )
        ).subscribe((res) => {
          if (res) {
            this.store.dispatch(
              StandingAuthCheckActions.callCheckInquiry({
                payload: res,
                viewType: 'standing-auth',
                transactionType: transactionTypes.check,
              })
            );
          }
        });
      } else if (event.rowData.Type.includes('ACH')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.ach,
          transferDirection: event.rowData.Type.includes('out')
            ? 'outgoing'
            : 'incoming',
          standingAuthId: event.rowData.StandingAuthId,
          frequency: event.rowData.isRecurring ? 'recurring' : 'onetime'
        };
        this.store.dispatch(
          SharedActions.updatePreviousContext({createType: this.createTypeInContext})
        );
        this.store.dispatch(
          StandingAuthorizationsTableActions.editProfileClicked({ rowData })
        );

        this.store.select(selectAchInstructions).pipe(
          take(1),
          withLatestFrom(this.store.select(selectAchRecurringProfiles)),
          map(([instructions, recurringProfiles]) => {
            return [...instructions, ...recurringProfiles]
          }),
          map((instructionsInclRecurring) =>
            instructionsInclRecurring.find((instructionOrProfile) => {
              return (
                instructionOrProfile.cashieringRqstId ===
                event.rowData['cashieringRqstId']
              );
            })
          )
        ).subscribe((res) => {
          if (res) {
            this.store.dispatch(
              StandingAuthAchActions.callAchInquiry({
                payload: res,
                viewType: 'standing-auth',
                transactionType: transactionTypes.ach,
              })
            );
          }
        });
      }
    }
  }

  startTransactionClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'StandingAuthTableStartTransaction'
      }));
    } else {
      if (event.rowData.Type.includes('ACH')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.ach,
          transferDirection: event.rowData.Type.includes('out')
            ? 'outgoing'
            : 'incoming',
          standingAuthId: event.rowData.StandingAuthId
        };
        this.store.dispatch(
          SharedActions.updatePreviousContext({createType: this.createTypeInContext})
        );
        this.store.dispatch(
          StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
        );
      } else if (event.rowData.Type.includes('Wire')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.wire,
          standingAuthId: event.rowData.StandingAuthId,
          isBundled: event.rowData.isBundled
        };
        this.store.dispatch(
          StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
        );
      } else if (event.rowData.Type.includes('Internal transfer')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.internalTransfer,
          standingAuthId: event.rowData.StandingAuthId,
          isBundled: event.rowData.isBundled
        };
        this.store.dispatch(
          StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
        );
      } else if (event.rowData.Type.includes('Check')) {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.check,
          standingAuthId: event.rowData.StandingAuthId,
          isBundled: event.rowData.isBundled
        };
        this.store.dispatch(
          StandingAuthorizationsTableActions.startTransactionClicked({ rowData })
        );
      }
    }
  }

  statusClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'StandingAuthTableViewDetails'
      }));
    } else {
      if (event.rowData.Type.includes('Internal transfer')) {
        this.store
          .pipe(
            takeUntil(this.destroy$),
            select(selectItStandingAuth),
            map((element) =>
              element.find((instruction) => {
                return (
                  instruction.cashieringRqstId ===
                  event.rowData['cashieringRqstId']
                );
              })
            )
          )
          .subscribe((res) => {
            if (res) {
              this.store.dispatch(
                StandingAuthorizationsTableActions.loadViewPage()
              );
              this.store.dispatch(
                InternalTransferActions.callInquiry({
                  payload: res,
                  viewType: 'standing-auth',
                  transactionType: transactionTypes.internalTransfer,
                })
              );
            }
          });
      } else if (event.rowData.Type.includes('ACH')) {
        this.store.select(selectAchInstructions).pipe(
          takeUntil(this.destroy$),
          withLatestFrom(this.store.select(selectAchRecurringProfiles)),
          map(([instructions, recurringProfiles]) => {
            return [...instructions, ...recurringProfiles]
          }),
          map((instructionsInclRecurring) =>
            instructionsInclRecurring.find((instructionOrProfile) => {
              return (
                instructionOrProfile.cashieringRqstId ===
                event.rowData['cashieringRqstId']
              );
            })
          )
        ).subscribe((res) => {
          if (res) {
            this.store.dispatch(
              StandingAuthorizationsTableActions.loadViewPage()
            );
            this.store.dispatch(
              SharedActions.updatePreviousContext({createType: this.createTypeInContext})
            );
            this.store.dispatch(
              AchActions.callInquiry({
                payload: res,
                viewType: 'standing-auth',
                transactionType: transactionTypes.ach,
              })
            );
          }
        });
      } else if (event.rowData.Type.includes('Wire')) {
        this.store
          .pipe(
            takeUntil(this.destroy$),
            select(selectWiresStandingAuth),
            map((element) =>
              element.find((instruction) => {
                return (
                  instruction.cashieringRqstId ===
                  event.rowData['cashieringRqstId']
                );
              })
            )
          )
          .subscribe((res) => {
            if (res) {
              this.store.dispatch(
                StandingAuthorizationsTableActions.loadViewPage()
              );
              this.store.dispatch(
                WireActions.callInquiry({
                  payload: res,
                  viewType: 'standing-auth',
                  transactionType: transactionTypes.wireSloa,
                  resourceVersion: '3'
                })
              );
            }
          });
      } else if (event.rowData.Type.includes('Check')) {
        this.store
          .pipe(
            takeUntil(this.destroy$),
            select(selectChecksStandingAuth),
            map((element) =>
              element.find((instruction) => {
                return (
                  instruction.cashieringRqstId ===
                  event.rowData['cashieringRqstId']
                );
              })
            )
          )
          .subscribe((res) => {
            if (res) {
              this.store.dispatch(
                StandingAuthorizationsTableActions.loadViewPage()
              );
              this.store.dispatch(
                CheckActions.callInquiry({
                  payload: res,
                  viewType: 'standing-auth',
                  transactionType: transactionTypes.check,
                })
              );
            }
          });
      }
    }
  }

  deleteProfileClicked(event: any)
  {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else{
      const rowData = event.rowData as StandingAuthTableColumn;
      this.currentSelectedTransaction$.next(rowData.StandingAuthId? rowData.StandingAuthId : null)
      this.deleteClicked$.next(true);
    }
  }

  getActions(instruction: StandingInstruction): string {
    let actions: string = '';

    if (instruction.isViewable) {
      actions += `<a>View details</a>`;
    }

    // rest of the actions can be appeneded here

    return actions;
  }

  buildColumnPreferences(preferences: ColumnState[]): ColDef[] {
    let result: ColDef[] = preferences.map(preference => {
      switch(preference.colId) {
        case 'Type':
          return {
            field: 'Type',
            filter: agFilterNames.set,
            floatingFilterComponent: 'customSetFloatingFilter',
            sort: preference.sort,
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 300,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Nickname':
          return {
            field: 'Nickname',
            floatingFilterComponent: 'customTextFloatingFilter',
            sort: preference.sort,
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            cellRenderer: SloaNicknameComponent,
            flex: 1,
            minWidth: 120,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'To/From':
          return {
            field: sharedColNames.toFromAccount,
            floatingFilterComponent: 'customTextFloatingFilter',
            sort: preference.sort,
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll('-', '')
            },
            flex: 1,
            minWidth: 450,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Status':
          return {
            field: sharedColNames.status,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 120,
            width: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Actions':
          return {
            field: this.colNames.actions,
            filter: false,
            suppressMovable: true,
            sortable: false,
            cellRenderer: ActionsLinkComponent,
            cellRendererParams: {
              onClick: this.actionLinkClicked.bind(this),
              viewStatus: tableActions.viewDetails,
              startTransaction: tableActions.startTransaction,
              editProfile: tableActions.edit,
              deleteProfile: tableActions.deleteProfile,
              deactivateProfile: tableActions.deactivateProfile,
              reactivateProfile: tableActions.reactivateProfile,
              tableName: 'standing authorizations'
            },
            minWidth: 305,
            maxWidth: 305
          }
        default:
          return {

        }
      }
    });
    return result;
  }

  mapToFromDisplayText(instruction: {
    to1: string;
    to2: string;
    to3: string;
    transactionType: TransactionType;
    address: string;
  }): string {
    switch (instruction.transactionType) {
      case transactionTypes.ach:
        return `${instruction.to1} Account: ${instruction.to2.trim()}`;
      case transactionTypes.check:
        return `${instruction.to1} ${instruction.to2} ${instruction.to3}`;
      case transactionTypes.internalTransfer:
        return `${instruction.to1}`;
      case transactionTypes.wire:
        return `${instruction.to1} Account: ${instruction.to3.trim()}`;
      default:
        return '';
    }
  }

  get createStandingAuthEnabled(): boolean {
    // added '|| this.isPtrs' for Bugfix SAC-290892, where some PTRS accounts were returning empty transactionAuthorizationDetails from the overview call
    return (this.hasCreateStandingAuthAccess || this.isEmployee || this.isPtrs);
  }

  scrollToTop(): void {
    const elem = document.getElementById('transaction-form-header');
    setTimeout(() => {
      elem?.focus();
      elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
    });
  }


  onCreateStandingAuthClick() {
    if (this.showCancelModalOnClick) {
      this.store.dispatch(displayCancelModal({
        nextUrlState: navRouteConstants.standingAuth,
        title: 'Cancel this transaction in progress?',
        content: ['This will take you to Create Standing Authorizations page and you will lose your transaction in progress.'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'StandingAuthTableStartTransaction'
      }));
    } else {
      const currentUrl: string = window.location.href;
      if (this.contextService.isMonolithUrl(currentUrl)) {
        this.router.navigate([navRouteConstants.standingAuth])
      }
      else
      {
        let encodedContext = decodeURIComponent(this.coreService.currentContext.encodedContext as string);
        this.router.navigate([navRouteConstants.standingAuth], { queryParams: { ec: encodedContext } });
      }
    }
  }

  private mapTransactionType(type: TransactionType, direction: TransactionDirection, descriptionSuffix: string, prefix: string, incomingAmount: number | undefined, outgoingAmount: number | undefined): string {
    let suffix = '';
    if (descriptionSuffix !== '') {
      suffix = ` - ${descriptionSuffix}`;
    }

    if (type === transactionTypes.internalTransfer) {
      return `Internal transfer${suffix}`;
    } else if (
      type === transactionTypes.ach &&
      (direction === transactionDirection.in || (incomingAmount != undefined && incomingAmount != null))
    ) {
      return `${prefix} ACH in${suffix}`;
    } else if (
      type === transactionTypes.ach &&
      (direction === transactionDirection.out || (outgoingAmount != undefined && outgoingAmount != null))
    ) {
      return `${prefix} ACH out${suffix}`;
    } else if (type === transactionTypes.check) {
      return `Check${suffix}`;
    } else if (type === transactionTypes.wire) {
      return `Wire${suffix}`;
    } else return type;
  }

  private isSloaAuthorized(transactionType: string): boolean {
    let sloaAuthorizations = this.transactionAuthorizationDetails.sloaAuthorization;

    if (transactionType === transactionTypes.check && sloaAuthorizations.includes(sloaAuthorization.check)) {
      return true;
    }
    if (transactionType === transactionTypes.internalTransfer && sloaAuthorizations.includes(sloaAuthorization.journals)) {
      return true;
    }
    if (transactionType === transactionTypes.wire && sloaAuthorizations.includes(sloaAuthorization.wire)) {
      return true;
    }
    if (transactionType === transactionTypes.ach && this.transactionAuthorizationDetails.moneylinkAuthorization.includes('useOnRequestSetup')) {
      return true;
    }
    else {
      return false;
    }
  }

  cancelClickedFromStandingAuthTable(source: cancelModalSourceTypes): boolean {
    return source === 'StandingAuthTableEdit' || source === 'StandingAuthTableViewDetails' || source === 'StandingAuthTableStartTransaction';
  }
}
